import React, { useState } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../../store/slices/authentication";
import { useGetMyBlockStatusQuery } from "../../../store/slices/api";
import { SharedHorizontalTabs, SharedCollapsibleIntro } from "../../shared";
import BlockedFeatureBanner from "../../shared/BlockedFeatureBanner";
import Inbox from "./Inbox";
import Compose from "./Compose";
import LetterThread from "./LetterThread";
import CorrespondenceSettings from "./CorrespondenceSettings";
import "./Correspondence.css";
import { getItem as safeGetItem, setItem as safeSetItem } from "../../../utils/safeStorage";

const TABS = [
  { key: "inbox", label: "Inbox" },
  { key: "sent", label: "Sent" },
  { key: "compose", label: "Compose" },
  { key: "settings", label: "Settings" },
];

const INTRO_TEXT = "text-[17px] font-body text-dupontBlue dark:text-darkText leading-relaxed";

const BANNER_DISMISSED_KEY = "dupont_correspondence_banner_dismissed";

// `embedded`: shown as a tab inside the Family Connect Hub (Sam, 2026-09-26).
// The hub already owns "tab" in the address, so the letter tabs use "letters"
// there, and the page title is left to the hub. /messages still works alone.
function Correspondence({ embedded = false }) {
  const param = embedded ? "letters" : "tab";
  const user = useSelector(selectUser);
  const individualId = user?.externalId ? parseInt(user.externalId) : null;
  const { data: blockStatus } = useGetMyBlockStatusQuery(individualId, {
    skip: !individualId,
  });
  const isMessagingBlocked = blockStatus?.is_messaging_blocked || false;

  const { currentTabIndex, setSearchParams } =
    SharedHorizontalTabs.useGetTabIndex(TABS, param);

  // Switch letter tabs. Embedded, keep the rest of the address (the hub's own
  // tab) and change only ours.
  const goToTab = (key) => {
    if (embedded) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(param, key);
          return next;
        },
        { replace: true }
      );
    } else {
      setSearchParams({ tab: key }, { replace: true });
    }
  };

  // Dismissible warning banner — only shows until user acknowledges
  const [bannerDismissed, setBannerDismissed] = useState(
    () => safeGetItem(BANNER_DISMISSED_KEY) === "true"
  );

  const handleDismissBanner = () => {
    setBannerDismissed(true);
    safeSetItem(BANNER_DISMISSED_KEY, "true");
  };

  // When a user clicks on a conversation row, we store its ID here
  const [openConversationId, setOpenConversationId] = useState(null);

  const handleTabChange = (index) => {
    setOpenConversationId(null);
    goToTab(TABS[index].key);
  };

  const handleOpenConversation = (conversationId) => {
    setOpenConversationId(conversationId);
  };

  const handleBackToList = () => {
    setOpenConversationId(null);
  };

  const handleConversationCreated = (conversationId) => {
    setOpenConversationId(conversationId);
    goToTab("inbox");
  };

  const renderTabContent = () => {
    // If a conversation is open (and we're not on the settings tab), show the letter thread
    if (openConversationId && currentTabIndex !== 3) {
      return (
        <LetterThread
          conversationId={openConversationId}
          individualId={individualId}
          onBack={handleBackToList}
        />
      );
    }

    switch (currentTabIndex) {
      case 1:
        return (
          <Inbox
            individualId={individualId}
            filter="sent"
            onOpenConversation={handleOpenConversation}
          />
        );
      case 2:
        if (isMessagingBlocked) {
          return (
            <BlockedFeatureBanner
              featureKey="is_messaging_blocked"
              featureName="Correspondence"
              individualId={individualId}
              mode="full"
            />
          );
        }
        return (
          <Compose
            individualId={individualId}
            onConversationCreated={handleConversationCreated}
          />
        );
      case 3:
        return <CorrespondenceSettings />;
      case 0:
      default:
        return (
          <Inbox
            individualId={individualId}
            filter="inbox"
            onOpenConversation={handleOpenConversation}
          />
        );
    }
  };

  return (
    <div className={embedded ? "flex flex-col" : "min-h-screen flex flex-col px-4 md:px-[60px] py-[30px] gap-y-[40px] bg-dupontCream dark:bg-darkPrimary transition-colors duration-200"}>
      {!embedded && (
        <div className="flex flex-col items-center">
          <div className="flex flex-col items-center text-center">
            <h1 className="text-[42px] font-display font-bold text-dupontBlue dark:text-darkText mb-[12px]">
              Correspondence
            </h1>
            <div className="w-[80px] h-[1px] bg-dupontGold"></div>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-y-[30px]">
        {/* Sam's landing copy (approved 2026-09-27), collapsed above the letter
            tabs the way the Bulletin and Marketplace show theirs. Part of
            Correspondence itself, so it shows once here and once inside the Hub. */}
        <SharedCollapsibleIntro
          heading="Private Letters Between Cousins"
          teaser="Write to anyone in the family, no email or phone number needed. Have someone in mind? Head over to the Compose tab."
        >
          <p className={INTRO_TEXT}>
            {"Remember when a letter from a cousin was an event? Correspondence brings a little of that back. It is a private way to write to anyone in the family, without needing their email or phone number first."}
          </p>
          <p className={INTRO_TEXT}>
            {"Only you and the person you write to can read what's inside a letter. You will see a small badge on the menu when something new arrives, and you can star a letter to keep it at the top. And if you would rather not receive letters at all, that is your choice in Settings."}
          </p>
          <p className={INTRO_TEXT}>
            {"Write to the cousin you keep meaning to catch up with. It might be the start of something great."}
          </p>
        </SharedCollapsibleIntro>

        <SharedHorizontalTabs.Component
          tabs={TABS.map((tab) => tab.label)}
          initialIndex={currentTabIndex}
          onChange={handleTabChange}
          className="flex-wrap gap-y-[8px]"
        />

        {/* Warning banner — shows on Inbox, Sent, Compose (not Settings) until acknowledged */}
        {!bannerDismissed && currentTabIndex !== 3 && (
          <div
            className="flex flex-col gap-y-[16px] p-[24px] rounded-[12px] border-2 border-dupontGold/40 bg-dupontGold/10 dark:bg-dupontGold/5"
          >
            <h3 className="text-[18px] font-display font-bold text-dupontBlue dark:text-dupontGold">
              Welcome to Correspondence
            </h3>
            <p className="text-[18px] font-body text-dupontBlue dark:text-darkText leading-relaxed">
              Your account automatically receives letters. These are a very
              non-invasive form of communication and are intended to make cousin
              connections easier. But if you would prefer radio silence, please
              go to your{" "}
              <span
                className="text-dupontGold font-semibold cursor-pointer underline"
                onClick={() => {
                  goToTab("settings");
                }}
              >
                settings
              </span>{" "}
              and turn off the corresponding toggle.
            </p>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleDismissBanner}
                className="px-[20px] py-[8px] text-[14px] font-display font-semibold rounded-full transition-colors duration-200 bg-dupontGold text-white hover:bg-dupontGold/90"
              >
                Got It
              </button>
            </div>
          </div>
        )}

        {renderTabContent()}
      </div>
    </div>
  );
}

export default Correspondence;
